import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  getStoredTokens,
  storeTokens,
  clearTokens,
  getBasicAuthHeader,
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
} from '../utils/tokenUtils';
import { shouldShowOnboardingForUser } from '../utils/onboardingUtils';
import { useCurrentUser } from './CurrentUserContext';
import { navigateTo } from '../../shared/services/NavigationService';
import { ADMIN_AUTH_EXPIRED_EVENT } from './fetchWithAuth';
import {
  ADMIN_NETWORK_ERROR_MESSAGE,
  isTransientNetworkError,
} from './networkErrors';
import { API_URL } from '../../config/env';

const AuthContext = createContext();

const BOOTSTRAP_BACKOFF_MS = [1000, 2000, 5000, 10000, 30000];
const MAX_BOOTSTRAP_ATTEMPTS = 8;
// storeTokens() writes the access and refresh keys as two separate localStorage writes, so a
// single token rotation in another tab arrives here as two storage events. Coalesce them, or
// every rotation costs each other open admin tab two /users/me calls instead of one.
const STORAGE_SYNC_DEBOUNCE_MS = 250;

export function AdminAuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  /** bootstrapping | authenticated | unauthenticated */
  const [authStatus, setAuthStatus] = useState('bootstrapping');
  const [loginPending, setLoginPending] = useState(false);
  const [error, setError] = useState(null);
  const [bootstrapAttempts, setBootstrapAttempts] = useState(0);
  const [bootstrapNonce, setBootstrapNonce] = useState(0);

  const { fetchCurrentUser, clearCurrentUser } = useCurrentUser();

  const loading = authStatus === 'bootstrapping' || loginPending;

  const logout = useCallback(() => {
    clearTokens();
    clearCurrentUser();
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    setAuthStatus('unauthenticated');
    setError(null);
    window.localStorage.setItem('admin_auth_sync', Date.now().toString());
  }, [clearCurrentUser]);

  const performTokenRefresh = useCallback(async () => {
    const { refreshToken: rt } = getStoredTokens();
    if (!rt) throw new Error('No refresh token');
    const form = new URLSearchParams();
    form.append('grant_type', 'refresh_token');
    form.append('refresh_token', rt);

    const res = await fetch(`${API_URL}/oauth/token`, {
      method: 'POST',
      headers: {
        Authorization: getBasicAuthHeader(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: form,
    });

    if (!res.ok) throw new Error('Token refresh failed');

    const data = await res.json();
    storeTokens(data.access_token, data.refresh_token);
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    setIsAuthenticated(true);
    window.localStorage.setItem('admin_auth_sync', Date.now().toString());
    return data.access_token;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const at = await performTokenRefresh();
      setAuthStatus('authenticated');
      return at;
    } catch (err) {
      logout();
      throw err;
    }
  }, [performTokenRefresh, logout]);

  const retryBootstrapNow = useCallback(() => {
    setBootstrapAttempts(0);
    setBootstrapNonce((n) => n + 1);
  }, []);

  // Session bootstrap (mount + cross-tab token changes + manual retry)
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;
    let attempt = 0;

    const clearRetryTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    async function tryOnce() {
      clearRetryTimer();

      const { accessToken: at, refreshToken: rt } = getStoredTokens();
      if (!at || !rt) {
        if (!cancelled) {
          setAccessToken(null);
          setRefreshToken(null);
          setIsAuthenticated(false);
          setAuthStatus('unauthenticated');
        }
        return;
      }

      if (!cancelled) {
        setAccessToken(at);
        setRefreshToken(rt);
        setAuthStatus('bootstrapping');
      }

      try {
        await fetchCurrentUser(at);
        if (!cancelled) {
          setIsAuthenticated(true);
          setAuthStatus('authenticated');
          attempt = 0;
          setBootstrapAttempts(0);
        }
      } catch (err) {
        if (cancelled) return;

        if (err.status === 401) {
          try {
            await performTokenRefresh();
            const { accessToken: newAt } = getStoredTokens();
            await fetchCurrentUser(newAt);
            if (!cancelled) {
              setIsAuthenticated(true);
              setAuthStatus('authenticated');
              attempt = 0;
              setBootstrapAttempts(0);
            }
          } catch {
            if (!cancelled) {
              logout();
            }
          }
          return;
        }

        if (isTransientNetworkError(err)) {
          if (attempt >= MAX_BOOTSTRAP_ATTEMPTS) {
            if (!cancelled) {
              setError(ADMIN_NETWORK_ERROR_MESSAGE);
            }
            return;
          }
          setBootstrapAttempts((n) => n + 1);
          const delay =
            BOOTSTRAP_BACKOFF_MS[
              Math.min(attempt, BOOTSTRAP_BACKOFF_MS.length - 1)
            ];
          attempt += 1;
          timeoutId = setTimeout(() => {
            tryOnce();
          }, delay);
          return;
        }

        if (typeof err.status === 'number') {
          if (!cancelled) {
            logout();
          }
          return;
        }

        if (attempt >= MAX_BOOTSTRAP_ATTEMPTS) {
          if (!cancelled) {
            setError(ADMIN_NETWORK_ERROR_MESSAGE);
          }
          return;
        }

        setBootstrapAttempts((n) => n + 1);
        const delay =
          BOOTSTRAP_BACKOFF_MS[
            Math.min(attempt, BOOTSTRAP_BACKOFF_MS.length - 1)
          ];
        attempt += 1;
        timeoutId = setTimeout(() => {
          tryOnce();
        }, delay);
      }
    }

    tryOnce();

    return () => {
      cancelled = true;
      clearRetryTimer();
    };
  }, [bootstrapNonce, fetchCurrentUser, performTokenRefresh, logout]);

  useEffect(() => {
    let timeoutId = null;

    const onStorage = (e) => {
      const isTokenKey = e.key === ACCESS_TOKEN_KEY || e.key === REFRESH_TOKEN_KEY;
      // e.key === null is localStorage.clear() in another tab.
      if (!isTokenKey && e.key !== null) return;
      // A write that did not change the value does not change our session either.
      if (isTokenKey && e.oldValue === e.newValue) return;

      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        setBootstrapNonce((n) => n + 1);
      }, STORAGE_SYNC_DEBOUNCE_MS);
    };

    window.addEventListener('storage', onStorage);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const revalidate = useCallback(async () => {
    const { accessToken: at, refreshToken: rt } = getStoredTokens();
    if (!at || !rt || authStatus !== 'authenticated') return;

    try {
      await fetchCurrentUser(at);
    } catch (err) {
      if (err.status === 401) {
        try {
          await performTokenRefresh();
          const { accessToken: newAt } = getStoredTokens();
          await fetchCurrentUser(newAt);
        } catch {
          logout();
        }
      }
    }
  }, [fetchCurrentUser, performTokenRefresh, logout, authStatus]);

  const lastHiddenAtRef = useRef(null);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = lastHiddenAtRef.current;
      lastHiddenAtRef.current = null;
      if (hiddenAt && Date.now() - hiddenAt > 5 * 60 * 1000) {
        revalidate();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [revalidate]);

  useEffect(() => {
    const onExpired = () => {
      logout();
      navigateTo('/admin/login/');
    };
    window.addEventListener(ADMIN_AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(ADMIN_AUTH_EXPIRED_EVENT, onExpired);
  }, [logout]);

  const login = useCallback(
    async (email, password, navigate, options = {}) => {
      setLoginPending(true);
      setError(null);
      try {
        const form = new URLSearchParams();
        form.append('grant_type', 'password');
        form.append('username', email);
        form.append('password', password);

        const res = await fetch(`${API_URL}/oauth/token`, {
          method: 'POST',
          headers: {
            Authorization: getBasicAuthHeader(),
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: form,
        });

        if (!res.ok) {
          if (res.status === 401 || res.status === 400) {
            const credErr = new Error('Invalid credentials');
            credErr.status = res.status;
            throw credErr;
          }
          const httpErr = new Error(`Login failed (${res.status})`);
          httpErr.status = res.status;
          throw httpErr;
        }

        const data = await res.json();
        storeTokens(data.access_token, data.refresh_token);
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);

        let userData;
        try {
          userData = await fetchCurrentUser(data.access_token);
        } catch (userErr) {
          if (userErr.status === 401) {
            clearTokens();
            clearCurrentUser();
            setAccessToken(null);
            setRefreshToken(null);
            setIsAuthenticated(false);
            setAuthStatus('unauthenticated');
            setError('Session could not be established. Please try again.');
            throw userErr;
          }
          if (isTransientNetworkError(userErr)) {
            setIsAuthenticated(true);
            setAuthStatus('authenticated');
            setError(
              'Could not load your profile. Check your connection and try again.'
            );
            throw userErr;
          }
          setIsAuthenticated(true);
          setAuthStatus('authenticated');
          setError('Could not load your profile.');
          throw userErr;
        }

        setIsAuthenticated(true);
        setAuthStatus('authenticated');
        setError(null);
        window.localStorage.setItem('admin_auth_sync', Date.now().toString());
        if (typeof navigate === 'function') {
          const needsOrgSetup = userData?.onboarding?.needs_org_setup;
          const defaultDestination = '/admin/dashboard/';
          const redirectTo = options.redirectTo || defaultDestination;

          if (needsOrgSetup) {
            navigate('/admin/register?step=3');
          } else if (shouldShowOnboardingForUser(userData)) {
            navigate('/admin/welcome', { state: { from: redirectTo } });
          } else {
            navigate(redirectTo);
          }
        }
        return true;
      } catch (err) {
        if (
          err.message === 'Invalid credentials' ||
          err.status === 401 ||
          err.status === 400
        ) {
          setError('Invalid email or password');
          setIsAuthenticated(false);
          setAuthStatus('unauthenticated');
        } else if (!getStoredTokens().accessToken) {
          if (isTransientNetworkError(err)) {
            setError(ADMIN_NETWORK_ERROR_MESSAGE);
          } else {
            setError(err.message || 'Login failed');
          }
          setIsAuthenticated(false);
          setAuthStatus('unauthenticated');
        }
        throw err;
      } finally {
        setLoginPending(false);
      }
    },
    [fetchCurrentUser, clearCurrentUser]
  );

  const applyTokens = useCallback((newAccessToken, newRefreshToken) => {
    storeTokens(newAccessToken, newRefreshToken);
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    const has = !!(newAccessToken && newRefreshToken);
    setIsAuthenticated(has);
    setAuthStatus(has ? 'authenticated' : 'unauthenticated');
    setError(null);
    window.localStorage.setItem('admin_auth_sync', Date.now().toString());
  }, []);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        refreshToken,
        isAuthenticated,
        loading,
        authStatus,
        bootstrapAttempts,
        retryBootstrapNow,
        error,
        login,
        logout,
        refresh,
        applyTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AuthContext);
}
